/**
 * Chart Utilities for Adaptive Profit Visualization
 * 
 * Provides utilities for determining optimal chart granularity,
 * aggregating data, and transforming data for chart rendering.
 */

/**
 * Granularity types for chart data
 */
export type ChartGranularity = 'hourly' | 'daily' | 'weekly' | 'monthly';

/**
 * Chart type options
 */
export type ChartType = 'bar' | 'line' | 'area';

/**
 * Configuration for chart rendering
 */
export interface ChartConfig {
  granularity: ChartGranularity;
  chartType: ChartType;
  dataPoints: number;
}

/**
 * Calculate the number of days between two dates (inclusive)
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Number of days (inclusive, so same day = 0)
 */
export function calculateDaysBetween(startDate: string, endDate: string): number {
  const start = new Date(startDate + 'T00:00:00Z');
  const end = new Date(endDate + 'T00:00:00Z');
  const diffMs = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return diffDays;
}

/**
 * Determine the optimal chart granularity and type based on date range
 * 
 * Rules:
 * - 1 day (same start/end): Hourly bar chart (24 bars)
 * - 2-7 days: Daily bar chart
 * - 8-30 days: Daily line chart
 * - 31-90 days: Daily line chart
 * - 91-180 days: Weekly line chart
 * - 181+ days: Weekly line chart
 * 
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Chart configuration with granularity, type, and expected data points
 */
export function determineGranularity(startDate: string, endDate: string): ChartConfig {
  const daysDiff = calculateDaysBetween(startDate, endDate);
  
  if (daysDiff === 0) {
    // Single day - hourly breakdown
    return { 
      granularity: 'hourly', 
      chartType: 'bar', 
      dataPoints: 24 
    };
  } else if (daysDiff < 7) {
    // 1-6 days - daily bars
    return { 
      granularity: 'daily', 
      chartType: 'bar', 
      dataPoints: daysDiff + 1 
    };
  } else if (daysDiff <= 7) {
    // Exactly 7 days - daily bars
    return { 
      granularity: 'daily', 
      chartType: 'bar', 
      dataPoints: daysDiff + 1 
    };
  } else if (daysDiff <= 30) {
    // 8-30 days - daily line chart
    return { 
      granularity: 'daily', 
      chartType: 'line', 
      dataPoints: daysDiff + 1 
    };
  } else if (daysDiff <= 90) {
    // 31-90 days - daily line chart
    return { 
      granularity: 'daily', 
      chartType: 'line', 
      dataPoints: daysDiff + 1 
    };
  } else if (daysDiff <= 180) {
    // 91-180 days - weekly line chart
    return { 
      granularity: 'weekly', 
      chartType: 'line', 
      dataPoints: Math.ceil((daysDiff + 1) / 7) 
    };
  } else {
    // 181+ days - weekly line chart
    return { 
      granularity: 'weekly', 
      chartType: 'line', 
      dataPoints: Math.ceil((daysDiff + 1) / 7) 
    };
  }
}

/**
 * Get a descriptive title for the chart based on granularity
 * @param granularity - Chart granularity
 * @param daysDiff - Number of days in the range
 * @returns Chart title string
 */
export function getChartTitle(granularity: ChartGranularity, daysDiff: number): string {
  switch (granularity) {
    case 'hourly':
      return 'HOURLY PROFIT TREND';
    case 'daily':
      if (daysDiff <= 7) {
        return `${daysDiff + 1}-DAY TREND`;
      } else if (daysDiff <= 30) {
        return `${daysDiff + 1}-DAY PROFIT TREND`;
      } else {
        return 'DAILY PROFIT TREND';
      }
    case 'weekly':
      return 'WEEKLY PROFIT TREND';
    case 'monthly':
      return 'MONTHLY PROFIT TREND';
    default:
      return 'PROFIT TREND';
  }
}
